import { useEffect } from 'react';

type ThemeMode = 'light' | 'dark';

type AuthPageThemeOptions = {
  forcedTheme?: ThemeMode;
};

const getStoredTheme = (): ThemeMode | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const stored = window.localStorage.getItem('theme');
  return stored === 'light' || stored === 'dark' ? stored : null;
};

const applyThemeClass = (theme: ThemeMode) => {
  if (typeof document === 'undefined') {
    return;
  }

  const root = document.documentElement;
  root.classList.remove('light', 'dark');
  root.classList.add(theme);
};

export function useAuthPageTheme(options: AuthPageThemeOptions = {}) {
  const { forcedTheme } = options;

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (forcedTheme) {
      applyThemeClass(forcedTheme);
      return;
    }

    const media = window.matchMedia('(prefers-color-scheme: dark)');

    const syncTheme = () => {
      const stored = getStoredTheme();
      applyThemeClass(stored ?? (media.matches ? 'dark' : 'light'));
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key === 'theme') {
        syncTheme();
      }
    };

    const handleMediaChange = () => {
      if (!getStoredTheme()) {
        syncTheme();
      }
    };

    syncTheme();

    window.addEventListener('storage', handleStorage);
    media.addEventListener('change', handleMediaChange);

    return () => {
      window.removeEventListener('storage', handleStorage);
      media.removeEventListener('change', handleMediaChange);
    };
  }, [forcedTheme]);
}
